// CRM-iframe read reporter.
//
// When the operator actually reads the open chat, the CRM should clear that
// lead's unread badge instantly. The reliable signal is the inbox read marker
// advancing (`lastReadInboxMessageId`) — telegram-tt only advances it once it
// has loaded the message list and sent `messages.readHistory`. We deliberately
// do NOT use chat *selection* (crmChatSelectReporter): selecting a dialog fires
// before the history loads / is marked read, so it isn't a "read" signal.
//
// Like crmChatSelectReporter (and unlike crmInboundReporter) this is a pure
// state change with no apiUpdate, so we subscribe via teactn's addCallback and
// diff the current chat's read marker. The parent SPA forwards `tg:chatRead` to
// POST /telegram-accounts/:id/chat-read. Best-effort: the worker's
// UpdateReadHistoryInbox sweep is the authoritative backstop.
//
// Lives in its own module (not crmBridge.ts) because crmBridge is imported by
// the gramjs worker too, where there is no global store / window. This file is
// imported only from index.tsx on the main thread.

import { getGlobal } from '../global';
import { selectCurrentMessageList } from '../global/selectors';
import { selectThreadReadState } from '../global/selectors/threads';
import { addCallback } from '../lib/teact/teactn';

import { getCrmAccountId, getCrmOrigin, isCrmIframeMode } from './crmBridge';
import { isUserId } from './entities/ids';

let isRegistered = false;
// Per-chat last reported read marker, so we only post on a forward jump (the
// genuine "operator just read further" event), not on every state tick.
const lastReportedReadByChat = new Map<string, number>();

function postToCrm(payload: object) {
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  try {
    window.parent.postMessage(payload, origin);
  } catch {
    // Best effort — never block the update pipeline on telemetry.
  }
}

export function registerCrmReadReporter() {
  if (isRegistered || !isCrmIframeMode()) return;
  isRegistered = true;

  addCallback((global: ReturnType<typeof getGlobal>) => {
    // Best-effort: a throw here would propagate into teactn's callback drain
    // and break other subscribers in the tick — so swallow everything.
    try {
      const current = selectCurrentMessageList(global);
      const chatId = current?.chatId;
      // 1:1 user DMs only (the CRM unread counter tracks lead conversations).
      if (!chatId || !isUserId(chatId) || chatId === global.currentUserId) return;

      const readMax = selectThreadReadState(global, chatId, current.threadId)?.lastReadInboxMessageId;
      if (!readMax) return;

      const prev = lastReportedReadByChat.get(chatId);
      if (prev !== undefined && readMax <= prev) return;
      lastReportedReadByChat.set(chatId, readMax);

      // maxId rides along so the backend only clears the badge once the read
      // reaches the latest inbound — a partial read just advances the watermark.
      postToCrm({
        type: 'tg:chatRead',
        payload: { accountId: getCrmAccountId(), peerId: chatId, maxId: readMax },
      });
    } catch {
      // never disrupt the state-callback pipeline
    }
  });
}
